import { createIntl, createIntlCache } from '@formatjs/intl';
import { catalogues } from './catalogues.js';
import { sourceLocale } from './locales.js';
/**
 * The i18n runtime (design §8), one module for the web app and the lanes, so a date or a message never differs between
 * a screen and an email. The web app imports it as `@trellistry/platform/i18n`, which pulls in nothing server-side.
 * Terminology sits under language: a church's words are arguments to a message, never a text substitution (§8.2).
 */
export * from './locales.js';
const timeZone = 'Asia/Singapore';
/** `group.one` → `groupOne`: how a term is named inside a message. */
export const termArgument = (key) => key.split('.').map((part, i) => (i === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1))).join('');
/** The words a church that has chosen none gets, per language (§8.2). */
export const defaultTerms = {
    'en-SG': { 'group.one': 'small group', 'group.plural': 'small groups', 'group.long': 'small group', 'children.ministry': 'Children’s Church' },
    'zh-Hans-SG': { 'group.one': '小组', 'group.plural': '小组', 'group.long': '小组', 'children.ministry': '儿童主日学' },
};
/**
 * A translator for one reader. A key missing in Chinese falls back to English (§8.1); a key missing altogether shows
 * the key, which a person will report, rather than a blank.
 */
export function createTranslator(locale, terms = {}, messages = catalogues) {
    const merged = { ...messages[sourceLocale], ...messages[locale] };
    const words = Object.fromEntries(Object.entries({ ...defaultTerms[locale], ...terms }).map(([k, v]) => [termArgument(k), v]));
    const intl = createIntl({ locale, messages: merged, timeZone, onError: () => { } }, createIntlCache());
    return (key, values = {}) => {
        if (!Object.hasOwn(merged, key))
            return key;
        return intl.formatMessage({ id: key }, { ...words, ...values });
    };
}
const parts = (date, locale, options) => Object.fromEntries(new Intl.DateTimeFormat(locale, { timeZone, ...options }).formatToParts(date).map((p) => [p.type, p.value]));
/** `Sun 10 Jan 2027`; in Chinese, the locale's own long form. */
export function formatDate(date, locale = sourceLocale) {
    if (locale !== sourceLocale)
        return new Intl.DateTimeFormat(locale, { timeZone, dateStyle: 'full' }).format(date);
    const p = parts(date, locale, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
    return `${p.weekday} ${p.day} ${p.month} ${p.year}`;
}
/** `5:00pm`; in Chinese, the locale's own short form. */
export function formatTime(date, locale = sourceLocale) {
    if (locale !== sourceLocale)
        return new Intl.DateTimeFormat(locale, { timeZone, timeStyle: 'short' }).format(date);
    const p = parts(date, locale, { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${p.hour}:${p.minute}${(p.dayPeriod ?? '').toLowerCase().replaceAll('.', '')}`;
}
/** `S$1,200`, with cents only when there are some. */
export function formatMoney(dollars) {
    const whole = Number.isInteger(dollars);
    return `S$${new Intl.NumberFormat('en-SG', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 }).format(dollars)}`;
}
//# sourceMappingURL=index.js.map